import React, { useEffect, useRef, useState } from 'react';

const INTRO_PARAGRAPHS = [
  'Flarix.ai ("Flarix", "we", "us", "our", or the "Company") respects your privacy and is committed to protecting the personal information entrusted to us.',
  'This Privacy Policy explains how we collect, use, process, store, disclose, and protect information when you access or use the Flarix.ai website, applications, software, AI-powered tools, floor-planning services, design services, and related services (collectively, the "Platform").',
  'This Privacy Policy should be read together with our Terms & Conditions and Disclaimer.',
  'By accessing or using Flarix.ai, you acknowledge that you have read and understood this Privacy Policy.',
];

const POLICY_SECTIONS = [
  {
    number: '01',
    title: 'Information We Collect',
    blocks: [
      { p: 'Depending on how you use Flarix.ai, we may collect the following categories of information.' },
      { h: '1.1 Information You Provide Directly' },
      { p: 'When you create an account, purchase a service, submit a design request, contact us, or otherwise interact with the Platform, we may collect:' },
      { list: ['Full name', 'Email address', 'Mobile or telephone number', 'Billing information', 'Account login information', 'Communication preferences', 'Location information provided by you', 'Property or project information', 'Plot dimensions', 'Plot shape', 'Road and site information', 'Number of floors', 'Room requirements', 'Design preferences', 'Vastu-related preferences', 'Budget information where voluntarily provided', 'Photographs', 'Drawings', 'Documents', 'Floor plans', 'Site images', 'Other information submitted through forms, questionnaires, uploads, or communications.'] },
      { p: 'You should not submit personal information belonging to another individual unless you have the necessary authorization to do so.' },
    ],
  },
  {
    number: '02',
    title: 'Information Collected Automatically',
    blocks: [
      { p: 'When you access or use the Platform, certain technical information may be collected automatically.' },
      { p: 'This may include:' },
      { list: ['IP address', 'Browser type', 'Operating system', 'Device type', 'Device identifiers', 'Approximate geographic location', 'Language preferences', 'Time zone', 'Pages visited', 'Features used', 'Search queries', 'Referring URLs', 'Date and time of access', 'Session information', 'Click and interaction data', 'Error logs', 'Other technical information necessary to operate and secure the Platform.'] },
      { p: 'This information may be collected through cookies, pixels, SDKs, analytics tools, server logs, and similar technologies.' },
    ],
  },
  {
    number: '03',
    title: 'Information About Your Property or Design Project',
    blocks: [
      { p: 'Flarix.ai may collect information relating to the property or project for which you request a design.' },
      { p: 'This may include:' },
      { list: ['Plot size', 'Plot dimensions', 'Site orientation', 'Location', 'Number of bedrooms', 'Number of bathrooms', 'Parking requirements', 'Floor requirements', 'Room preferences', 'Family requirements', 'Design preferences', 'Architectural preferences', 'Vastu preferences', 'Uploaded site photographs', 'Existing floor plans', 'Other project-specific information.'] },
      { p: 'Such information may be necessary to generate or improve the requested design output.' },
    ],
  },
  {
    number: '04',
    title: 'Payment Information',
    blocks: [
      { p: 'Payments may be processed through third-party payment gateways.' },
      { p: 'Depending on the payment method and provider, payment information may include:' },
      { list: ['Transaction amount', 'Transaction ID', 'Payment status', 'Payment method', 'Billing information', 'Other information necessary to confirm the transaction.'] },
      { p: 'Unless expressly stated otherwise, Flarix does not intend to store complete credit or debit card numbers or other sensitive payment credentials on its own servers.' },
      { p: 'Payment information may be processed directly by third-party payment service providers in accordance with their respective privacy policies and security practices.' },
    ],
  },
  {
    number: '05',
    title: 'Cookies and Tracking Technologies',
    blocks: [
      { p: 'Flarix.ai may use cookies and similar technologies to operate and improve the Platform.' },
      { p: 'Cookies may be used for:' },
      { list: ['Authentication', 'Security', 'Remembering preferences', 'Maintaining sessions', 'Analytics', 'Measuring advertising performance', 'Understanding user behaviour', 'Improving the Platform', 'Personalizing experiences', 'Marketing and remarketing, where permitted by applicable law.'] },
      { p: 'You may be able to control cookies through your browser or device settings.' },
      { p: 'Disabling certain cookies may affect the functionality of the Platform.' },
    ],
  },
  {
    number: '06',
    title: 'How We Use Your Information',
    blocks: [
      { p: 'We may use personal information for legitimate and permitted purposes, including:' },
      { h: 'Providing our services' },
      { p: 'To:' },
      { list: ['Generate AI-assisted floor plans', 'Process design requests', 'Deliver purchased products', 'Maintain user accounts', 'Process transactions', 'Provide customer support', 'Respond to enquiries', 'Communicate regarding your orders or services.'] },
      { h: 'Improving Flarix.ai' },
      { p: 'We may use information to:' },
      { list: ['Improve Platform functionality', 'Identify technical problems', 'Improve user experience', 'Analyze usage patterns', 'Improve our AI-powered tools', 'Develop new products and features', 'Measure product performance.'] },
      { h: 'Security and fraud prevention' },
      { p: 'We may process information to:' },
      { list: ['Detect fraudulent activity', 'Prevent unauthorized access', 'Protect users and our systems', 'Investigate abuse', 'Maintain Platform security', 'Enforce our Terms & Conditions.'] },
      { h: 'Communications' },
      { p: 'We may use your contact information to send:' },
      { list: ['Transactional communications', 'Account notifications', 'Service updates', 'Support communications', 'Important policy updates', 'Marketing communications where permitted by law and subject to applicable preferences or consent requirements.'] },
    ],
  },
  {
    number: '07',
    title: 'AI Processing',
    blocks: [
      { p: 'Flarix.ai uses artificial intelligence and automated technologies to provide certain services.' },
      { p: 'Information you provide may be processed by our systems and, where necessary, by third-party AI, cloud, hosting, analytics, or technology providers acting on our behalf.' },
      { p: 'For example, information concerning your plot, room requirements, design preferences, or other project specifications may be processed to generate a floor plan or other design output.' },
      { p: 'We take reasonable steps to ensure that information is processed only for legitimate business and service-related purposes.' },
    ],
  },
  {
    number: '08',
    title: 'Use of Data to Improve AI and Services',
    blocks: [
      { p: 'Subject to applicable law and the permissions, notices, or consent that may be required, Flarix may use certain information to:' },
      { list: ['Improve the accuracy and performance of its services', 'Identify errors', 'Improve product functionality', 'Develop new features', 'Analyze aggregated usage patterns', 'Improve AI-assisted design systems.'] },
      { p: 'Where reasonably practicable, Flarix may use aggregated, statistical, or de-identified information for analytics, research, product development, and business purposes.' },
      { p: 'We will not represent that information is anonymized where it remains reasonably capable of being associated with an identifiable individual.' },
    ],
  },
  {
    number: '09',
    title: 'How We Share Information',
    blocks: [
      { p: 'We may share information with selected third parties where reasonably necessary to operate the Platform or provide requested services.' },
      { p: 'These may include:' },
      { list: ['Cloud hosting providers', 'AI and technology providers', 'Payment processors', 'Email and communication providers', 'Customer-support platforms', 'Analytics providers', 'Advertising and marketing technology providers', 'Fraud-prevention providers', 'Security providers', 'Professional advisers', 'Contractors and service providers', 'Government or regulatory authorities where legally required.'] },
      { p: 'We do not sell your personal information as a standalone commercial product.' },
    ],
  },
  {
    number: '10',
    title: 'Third-Party Service Providers',
    blocks: [
      { p: 'Flarix may use third-party service providers to support its operations.' },
      { p: 'Examples may include providers for:' },
      { list: ['Cloud hosting', 'Artificial intelligence', 'Payment processing', 'Email', 'SMS', 'WhatsApp or similar communications', 'Analytics', 'Advertising', 'Customer support', 'Authentication', 'Data storage', 'Security.'] },
      { p: "These providers may process information on Flarix's behalf and may have their own privacy policies." },
      { p: 'Where required, Flarix will take appropriate contractual or other reasonable measures regarding the handling of personal information.' },
    ],
  },
  {
    number: '11',
    title: 'Advertising and Marketing',
    blocks: [
      { p: 'Flarix may use advertising and analytics platforms to understand how users interact with our advertising and Platform.' },
      { p: 'This may include technologies such as:' },
      { list: ['Cookies', 'Pixels', 'Conversion tracking', 'Advertising identifiers', 'Remarketing technologies', 'Analytics tools.'] },
      { p: 'These technologies may help us measure campaigns, understand user journeys, and improve advertising relevance.' },
      { p: 'Where required by applicable law, we will obtain appropriate consent or provide applicable opt-out mechanisms.' },
    ],
  },
  {
    number: '12',
    title: 'Data Retention',
    blocks: [
      { p: 'We retain personal information only for as long as reasonably necessary for the purposes for which it was collected, including:' },
      { list: ['Providing services', 'Maintaining business records', 'Resolving disputes', 'Preventing fraud', 'Maintaining security', 'Complying with legal obligations', 'Enforcing contractual rights.'] },
      { p: 'Retention periods may vary depending on the type of information and the purpose for which it is processed.' },
      { p: 'When personal information is no longer reasonably required, we may delete, anonymize, or otherwise dispose of it in accordance with applicable law.' },
    ],
  },
  {
    number: '13',
    title: 'Data Security',
    blocks: [
      { p: 'Flarix takes reasonable technical and organizational measures designed to protect personal information against unauthorized access, loss, misuse, alteration, disclosure, or destruction.' },
      { p: 'Depending on the nature of the information and our systems, these measures may include:' },
      { list: ['Access controls', 'Authentication mechanisms', 'Encryption or secure transmission', 'Security monitoring', 'Role-based access', 'Infrastructure security', 'Backup procedures', 'Internal security practices.'] },
      { p: 'However, no internet transmission or electronic storage system can be guaranteed to be completely secure.' },
      { p: 'Accordingly, we cannot guarantee absolute security of your information.' },
    ],
  },
  {
    number: '14',
    title: 'Data Breaches',
    blocks: [
      { p: 'If Flarix becomes aware of a personal-data breach that requires notification under applicable law, we will take appropriate steps to investigate, contain, mitigate, and report the incident as required by applicable law.' },
    ],
  },
  {
    number: '15',
    title: 'International Data Transfers',
    blocks: [
      { p: 'Some of our technology, cloud, AI, analytics, payment, or service providers may process information outside India.' },
      { p: 'Where personal information is transferred or processed across borders, Flarix will take steps required under applicable law and applicable contractual or organizational safeguards.' },
    ],
  },
  {
    number: '16',
    title: 'Your Rights',
    blocks: [
      { p: 'Subject to applicable law, you may have rights relating to your personal information, which may include:' },
      { list: ['Requesting information about processing of your personal data', 'Requesting access to certain personal information', 'Requesting correction of inaccurate information', 'Requesting deletion or erasure where applicable', 'Requesting withdrawal of consent where processing is based on consent', 'Requesting information regarding certain processing activities', 'Exercising applicable grievance or complaint rights', 'Opting out of certain marketing communications.'] },
      { p: 'The availability and scope of these rights may depend on applicable law and the circumstances of processing.' },
    ],
  },
  {
    number: '17',
    title: 'Withdrawal of Consent',
    blocks: [
      { p: 'Where Flarix processes personal information based on your consent, you may withdraw your consent subject to applicable law.' },
      { p: 'Withdrawal of consent will not affect the lawfulness of processing that occurred before withdrawal.' },
      { p: 'In some circumstances, withdrawal of consent may prevent Flarix from continuing to provide certain services or features.' },
    ],
  },
  {
    number: '18',
    title: 'Marketing Communications',
    blocks: [
      { p: 'You may opt out of promotional emails or other marketing communications by:' },
      { list: ['Using the unsubscribe mechanism provided in the communication', 'Updating applicable communication preferences', 'Contacting us using the details provided below.'] },
      { p: 'You may continue to receive essential transactional, security, account, or service-related communications even after opting out of marketing communications.' },
    ],
  },
  {
    number: '19',
    title: "Children's Privacy",
    blocks: [
      { p: 'Flarix.ai is not intended to knowingly collect personal information from children in violation of applicable law.' },
      { p: 'If you believe that a child has provided personal information to Flarix in circumstances where such collection was not permitted, please contact us so that we can take appropriate action.' },
    ],
  },
  {
    number: '20',
    title: 'User-Submitted Photographs and Documents',
    blocks: [
      { p: 'If you upload photographs, drawings, floor plans, property documents, or other materials, you are responsible for ensuring that you have the right to provide such materials to Flarix.' },
      { p: 'Please avoid uploading unnecessary personal or sensitive information.' },
      { p: 'Flarix may process uploaded materials for the purposes of providing the requested service and related legitimate business purposes as described in this Privacy Policy.' },
    ],
  },
  {
    number: '21',
    title: 'Third-Party Websites',
    blocks: [
      { p: 'The Platform may contain links to websites or services operated by third parties.' },
      { p: 'Flarix is not responsible for the privacy practices, security, content, or policies of third-party websites.' },
      { p: 'You should review the privacy policy of any third-party service before providing personal information.' },
    ],
  },
  {
    number: '22',
    title: 'Business Transfers',
    blocks: [
      { p: 'If Flarix is involved in a merger, acquisition, restructuring, financing, sale of assets, or similar corporate transaction, personal information may be transferred as part of that transaction, subject to applicable law.' },
      { p: 'Any successor entity may be required to continue protecting personal information in accordance with applicable legal obligations.' },
    ],
  },
  {
    number: '23',
    title: 'Legal Disclosures',
    blocks: [
      { p: 'Flarix may disclose personal information where reasonably necessary to:' },
      { list: ['Comply with applicable law', 'Respond to lawful government requests', 'Comply with court orders', 'Protect the rights, property, or safety of Flarix', 'Investigate fraud or security incidents', 'Enforce our Terms & Conditions', 'Protect users or the public from unlawful activity.'] },
    ],
  },
  {
    number: '24',
    title: 'Changes to This Privacy Policy',
    blocks: [
      { p: 'We may update this Privacy Policy periodically.' },
      { p: 'When we make changes, we will update the "Last Updated" date at the beginning of this Policy.' },
      { p: 'Where required by applicable law, we may provide additional notice or obtain consent for material changes.' },
      { p: 'You are encouraged to periodically review this Privacy Policy.' },
    ],
  },
  {
    number: '25',
    title: 'Grievance Redressal',
    blocks: [
      { p: 'If you have any question, concern, complaint, or grievance regarding the processing of your personal information, you may contact our designated privacy/grievance contact.' },
      { contact: [
        { label: 'Grievance / Privacy Contact', value: '[Name / Department]' },
        { label: 'Phone', value: '[Phone Number]' },
        { label: 'Address', value: '608, Lotus divine, Shivalaya, Bijalpur, Indore' },
      ] },
      { p: 'We will review and address grievances in accordance with applicable law.' },
    ],
  },
  {
    number: '26',
    title: 'Data Principal / User Requests',
    blocks: [
      { p: 'For privacy-related requests, please provide sufficient information to allow us to verify your identity and locate the relevant information.' },
      { p: 'We may request additional information where reasonably necessary to prevent unauthorized disclosure or fraudulent requests.' },
      { p: 'We will respond to valid requests within the time period required by applicable law.' },
    ],
  },
  {
    number: '27',
    title: 'Contact Us',
    blocks: [
      { p: 'For questions about this Privacy Policy or our privacy practices, please contact:' },
      { contact: [
        { label: 'Legal Entity', value: 'flarix.ai' },
        { label: 'Registered Address', value: '608, Lotus divine, Shivalaya, Bijalpur, Indore' },
        { label: 'Email', value: 'privacy@flarix.ai' },
        { label: 'Website', value: 'https://flarix.ai' },
      ] },
    ],
  },
  {
    number: '28',
    title: 'Acknowledgement',
    blocks: [
      { p: 'By using Flarix.ai, you acknowledge that you have read and understood this Privacy Policy and understand how your personal information may be collected and processed as described above.' },
      { p: 'Where applicable, Flarix will seek consent or provide other legally required notices and choices before processing personal information.' },
    ],
  },
];

function renderBlock(block, key) {
  if (block.p) return <p className="pp-text" key={key}>{block.p}</p>;
  if (block.h) return <h3 className="pp-subhead" key={key}>{block.h}</h3>;
  if (block.list) {
    return (
      <ul className="pp-list" key={key}>
        {block.list.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    );
  }
  if (block.contact) {
    return (
      <div className="pp-contact" key={key}>
        {block.contact.map((row, i) => (
          <div className="pp-contact-row" key={i}>
            <span className="pp-contact-label">{row.label}</span>
            <span className="pp-contact-value">{row.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
}

function PrivacyPolicy() {
  const [activeId, setActiveId] = useState(POLICY_SECTIONS[0].number);
  const sectionRefs = useRef({});

  useEffect(() => {
    if (!('IntersectionObserver' in window)) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.dataset.number);
          }
        });
      },
      { rootMargin: '-15% 0px -70% 0px', threshold: 0 }
    );
    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="privacy-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');

        .privacy-page {
          --ink: #0b1d2e;
          --violet: #8b5cf6;
          --cyan: #5ec8e8;
          --cyan-deep: #0e7490;
          --line: rgba(94, 200, 232, 0.14);
          --line-strong: rgba(94, 200, 232, 0.4);
          --text: #2b2740;
          --text-muted: #6b7280;
          --border: #ece9f7;
          --panel: #f7f6fd;
          font-family: 'Poppins', sans-serif;
          background: #fff;
          color: var(--text);
          line-height: 1.75;
          width: 100%;
          overflow-x: hidden;
        }
        .privacy-page * {
          box-sizing: border-box;
        }

        .privacy-page .pp-hero {
          position: relative;
          overflow: hidden;
          background: var(--ink);
          background-image:
            repeating-linear-gradient(0deg, var(--line) 0 1px, transparent 1px 42px),
            repeating-linear-gradient(90deg, var(--line) 0 1px, transparent 1px 42px);
          padding: 5rem 1.5rem 6.5rem;
          text-align: center;
          border-bottom: 1px solid var(--line-strong);
        }

        .privacy-page .pp-corner {
          position: absolute;
          width: 18px;
          height: 18px;
          z-index: 1;
        }
        .privacy-page .pp-corner::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          height: 1px;
          background: var(--line-strong);
        }
        .privacy-page .pp-corner::after {
          content: '';
          position: absolute;
          left: 50%;
          top: 0;
          bottom: 0;
          width: 1px;
          background: var(--line-strong);
        }
        .privacy-page .pp-corner--tl { top: 20px; left: 20px; }
        .privacy-page .pp-corner--tr { top: 20px; right: 20px; }
        .privacy-page .pp-corner--bl { bottom: 20px; left: 20px; }
        .privacy-page .pp-corner--br { bottom: 20px; right: 20px; }

        .privacy-page .pp-logo-wrap {
          position: relative;
          z-index: 2;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.9rem 1.4rem;
          background: rgba(94, 200, 232, 0.05);
          border: 1px solid var(--line-strong);
          border-radius: 8px;
          margin-bottom: 1.75rem;
        }
        .privacy-page .pp-logo-wrap img {
          height: 40px;
          width: auto;
        }

        .privacy-page .pp-eyebrow {
          position: relative;
          z-index: 2;
          display: inline-block;
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: var(--cyan);
          margin: 0 0 0.6rem;
        }

        .privacy-page .pp-hero h1 {
          position: relative;
          z-index: 2;
          font-size: clamp(2.2rem, 5vw, 3.1rem);
          font-weight: 800;
          letter-spacing: -0.01em;
          color: #fff;
          text-shadow: 0 2px 30px rgba(94, 200, 232, 0.25);
          margin: 0 0 0.9rem;
        }

        .privacy-page .pp-hero-sub {
          position: relative;
          z-index: 2;
          max-width: 520px;
          margin: 0 auto 1.5rem;
          color: rgba(220, 238, 246, 0.75);
          font-size: 1rem;
          font-weight: 400;
        }

        .privacy-page .pp-updated-pill {
          position: relative;
          z-index: 2;
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          background: rgba(94, 200, 232, 0.08);
          border: 1px solid var(--line-strong);
          color: var(--cyan);
          font-size: 0.82rem;
          font-weight: 500;
          padding: 0.4rem 0.9rem;
          border-radius: 999px;
        }

        .privacy-page .pp-body {
          max-width: 1120px;
          margin: -3rem auto 0;
          padding: 0 1.5rem 5rem;
          position: relative;
          z-index: 3;
        }

        .privacy-page .pp-lead {
          background: #fff;
          border: 1px solid var(--border);
          border-radius: 20px;
          box-shadow: 0 20px 45px -25px rgba(76, 29, 149, 0.35);
          padding: 2.25rem 2.5rem;
          margin-bottom: 2.5rem;
        }
        .privacy-page .pp-lead p {
          margin: 0 0 0.9rem;
          color: var(--text-muted);
          font-size: 0.98rem;
        }
        .privacy-page .pp-lead p:last-child {
          margin-bottom: 0;
        }

        .privacy-page .pp-layout {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
          min-width: 0;
        }
        @media (min-width: 1024px) {
          .privacy-page .pp-layout {
            grid-template-columns: 250px 1fr;
            align-items: start;
          }
        }

        .privacy-page .pp-index {
          background: var(--panel);
          border: 1px solid var(--border);
          border-radius: 18px;
          padding: 1.25rem;
          min-width: 0;
        }
        @media (min-width: 1024px) {
          .privacy-page .pp-index {
            position: sticky;
            top: 1.5rem;
            max-height: calc(100vh - 3rem);
            overflow-y: auto;
          }
        }

        .privacy-page .pp-index-label {
          display: block;
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--text-muted);
          margin: 0 0 0.85rem;
          padding: 0 0.4rem;
        }

        .privacy-page .pp-index ul {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 0.15rem;
        }
        @media (max-width: 1023px) {
          .privacy-page .pp-index ul {
            flex-direction: row;
            overflow-x: auto;
            gap: 0.5rem;
            padding-bottom: 0.25rem;
          }
          .privacy-page .pp-index a {
            white-space: nowrap;
          }
        }

        .privacy-page .pp-index a {
          display: flex;
          align-items: center;
          gap: 0.55rem;
          font-size: 0.82rem;
          font-weight: 500;
          color: var(--text-muted);
          text-decoration: none;
          padding: 0.5rem 0.6rem;
          border-radius: 10px;
        }
        .privacy-page .pp-index a:hover {
          background: #fff;
          color: var(--cyan-deep);
        }
        .privacy-page .pp-index a.is-active {
          background: #fff;
          color: var(--cyan-deep);
          font-weight: 600;
          box-shadow: 0 4px 12px -6px rgba(14, 116, 144, 0.5);
        }
        .privacy-page .pp-index a span {
          font-size: 0.68rem;
          font-weight: 700;
          color: var(--violet);
        }

        .privacy-page .pp-sections {
          display: flex;
          flex-direction: column;
          gap: 1.35rem;
          min-width: 0;
        }

        .privacy-page .pp-section {
          background: #fff;
          border: 1px solid var(--border);
          border-radius: 18px;
          padding: 1.85rem 2rem;
          scroll-margin-top: 1.5rem;
        }

        .privacy-page .pp-section-head {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.1rem;
        }
        .privacy-page .pp-section-number {
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 12px;
          background: linear-gradient(135deg, var(--violet), var(--cyan-deep));
          color: #fff;
          font-weight: 700;
          font-size: 0.95rem;
        }
        .privacy-page .pp-section-title {
          font-size: 1.15rem;
          font-weight: 700;
          color: var(--ink);
          margin: 0;
        }

        .privacy-page .pp-subhead {
          font-size: 0.92rem;
          font-weight: 600;
          color: var(--cyan-deep);
          margin: 1.1rem 0 0.5rem;
        }

        .privacy-page .pp-text {
          margin: 0 0 0.75rem;
          color: var(--text);
          font-size: 0.96rem;
        }
        .privacy-page .pp-text:last-child {
          margin-bottom: 0;
        }

        .privacy-page .pp-list {
          margin: 0 0 0.75rem;
          padding: 0;
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 0.45rem;
        }
        .privacy-page .pp-list:last-child {
          margin-bottom: 0;
        }
        .privacy-page .pp-list li {
          position: relative;
          padding-left: 1.15rem;
          font-size: 0.94rem;
          color: var(--text);
        }
        .privacy-page .pp-list li::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0.55em;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--violet);
        }

        .privacy-page .pp-contact {
          background: var(--panel);
          border: 1px solid var(--border);
          border-radius: 14px;
          padding: 1.1rem 1.3rem;
          margin: 0.5rem 0 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.55rem;
        }
        .privacy-page .pp-contact-row {
          display: flex;
          flex-wrap: wrap;
          gap: 0.4rem;
          font-size: 0.9rem;
        }
        .privacy-page .pp-contact-label {
          font-weight: 600;
          color: var(--ink);
        }
        .privacy-page .pp-contact-value {
          color: var(--text-muted);
          font-style: italic;
        }

        .privacy-page .pp-footer {
          text-align: center;
          margin-top: 3rem;
          padding-top: 2rem;
          border-top: 1px solid var(--border);
          color: var(--text-muted);
          font-size: 0.85rem;
        }

        @media (max-width: 640px) {
          .privacy-page .pp-hero {
            padding: 4rem 1.25rem 6rem;
          }
          .privacy-page .pp-lead,
          .privacy-page .pp-section {
            padding: 1.5rem 1.4rem;
          }
        }
      `}</style>

      <div className="pp-hero">
        <span className="pp-corner pp-corner--tl" aria-hidden="true" />
        <span className="pp-corner pp-corner--tr" aria-hidden="true" />
        <span className="pp-corner pp-corner--bl" aria-hidden="true" />
        <span className="pp-corner pp-corner--br" aria-hidden="true" />
        <div className="pp-logo-wrap">
          <img loading="lazy" src="/images/logo.png" className="img-fluid" alt="Flarix.ai" />
        </div>
        <h1>Privacy Policy</h1>
        <p className="pp-hero-sub">
          How Flarix.ai collects, uses, and protects your information across our AI floor-planning
          and design services.
        </p>

      </div>

      <div className="pp-body">
        <div className="pp-lead">
          {INTRO_PARAGRAPHS.map((text, i) => (
            <p key={i}>{text}</p>
          ))}
        </div>

        <div className="pp-layout">
          <nav className="pp-index" aria-label="Policy sections">
            <span className="pp-index-label">On this page</span>
            <ul>
              {POLICY_SECTIONS.map((s) => (
                <li key={s.number}>
                  <a
                    href={`#section-${s.number}`}
                    className={activeId === s.number ? 'is-active' : ''}
                  >
                    <span>{s.number}</span>
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="pp-sections">
            {POLICY_SECTIONS.map((section) => (
              <section
                id={`section-${section.number}`}
                className="pp-section"
                key={section.number}
                data-number={section.number}
                ref={(el) => (sectionRefs.current[section.number] = el)}
              >
                <div className="pp-section-head">
                  <span className="pp-section-number">{section.number}</span>
                  <h2 className="pp-section-title">{section.title}</h2>
                </div>
                {section.blocks.map((block, i) => renderBlock(block, i))}
              </section>
            ))}
          </div>
        </div>

        <div className="pp-footer">© Flarix.ai — All rights reserved.</div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;